'use client';

import { FactiiiRequirement, type FactiiiTag, type PinnedFactiii } from '@shared/all/domains/factiiis';
import type { Post } from '@shared/all/domains/posts';
import { type Upload as UploadType } from '@shared/all/domains/uploads';
import { DROPZONE_ACCEPT } from '@shared/all/uploadTypes';
import type { CrossPostPlatform } from '@shared/all/validators';
import { cameraFactiiiFor } from '@shared/utils/helpers/cameraFactiii';
import { calculateNewPostTronCostBreakdown, convertPinnedFactiiiToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import toast from '@shared/utils/helpers/toast';
import { getDimensionsFromUri, getVideoDurationFromUri } from '@shared/utils/helpers/uploadHelper';
import useDefaultFactiiis from '@shared/utils/hooks/useDefaultFactiiis';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUpload from '@shared/utils/hooks/useUpload';
import useUser from '@shared/utils/hooks/useUser';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import eventEmitter from '../../../helpers/eventEmitter';
import { useConfirmIfPrivate } from '../../../hooks/useConfirmIfPrivate';
import { useTronAffordability } from '../../../hooks/useTronAffordability';
import { client } from '../../../utils/trpc';
import { trackActivation } from '../../onboarding/trackOnboarding';
import type { DraftItemSelectHandler, NewPostContextValue } from './NewPostContext';
const SCHEDULED_POSTS_PATH = '/scheduled';

// The server's limit on a post's content (posts.new).
const MAX_CONTENT_LENGTH = 5000;
type UseNewPostProps = {
  closeHandler: (arg0?: boolean) => void;
  spaceSlug?: string;
  pinnedFactiii?: PinnedFactiii;
  uniqueId?: string;
  initialLocation?: {
    latitude: number;
    longitude: number;
  } | null;
  onPostCreated?: (postId: string, latitude?: number, longitude?: number) => void;
  inline?: boolean;
  isBoardPost?: boolean;
  parentPostId?: string;
  parentPost?: Post;
  isPrivatePost?: boolean;
  // A post the pinned factiii's tag cites from the start, for a factiii with
  // POST_REFERENCE_REQUIRED (a Company Response cites the post it answers).
  referencedPostId?: string;
};
export const useNewPost = (props: UseNewPostProps) => {
  const {
    currentUser
  } = useUser();
  const {
    checkRequirementsMet,
    remappedAddFactiiis
  } = useFactiiiHandlers();
  const replyUniqueId = props.uniqueId ?? (props.parentPostId && !props.inline ? props.parentPostId : undefined);
  const {
    queryKey: postsQueryKey,
    appendPost,
    appendReply
  } = usePostsShared(client, replyUniqueId);
  const defaultFactiiiIds = useDefaultFactiiis(client);
  const {
    localPreferenceState
  } = useLocalPreference();
  const {
    files,
    uploadingKeys,
    addFile,
    removeFile,
    clear,
    uploadOne,
    upload
  } = useUpload(client);
  const {
    createPrivatePost
  } = usePostsShared(client);
  const {
    ensureConfirmed,
    dialog: privateConfirmDialog
  } = useConfirmIfPrivate();
  const isReplyMode = Boolean(props.parentPostId && props.inline);
  const isTopLevelReplyModal = Boolean(props.parentPostId && !props.inline);
  const scrollToReply = (replyId: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(`reply-${replyId}`);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });
  };
  const queryClient = useQueryClient();
  const router = useRouter();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [myPostFactiiiCounts, setMyPostFactiiiCounts] = useState<FactiiiTag[]>([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [saveDraftConfModalOpen, setSaveDraftConfModalOpen] = useState(false);
  const [draftsListModalOpen, setDraftsListModalOpen] = useState(false);
  const [moderationConfModalOpen, setModerationConfModalOpen] = useState(false);
  const [refDraftPostId, setRefDraftPostId] = useState<string | undefined>(undefined);
  // Open from the start when the tag arrives already citing a post, so the
  // person sees what their reply is tagged with before they send it.
  const [factiiiOpen, setFactiiiOpen] = useState(Boolean(props.referencedPostId));
  const [anonymousPost, setAnonymousPost] = useState(false);
  const [privatePost, setPrivatePost] = useState(Boolean(props.parentPostId && props.isPrivatePost) || localPreferenceState.postModeType === 'private');
  const [autoProcess, setAutoProcess] = useState(Boolean(props.pinnedFactiii && props.pinnedFactiii.requirements.length > 0 && props.pinnedFactiii.requirements.every(requirement => requirement === FactiiiRequirement.CAMERA)));
  const [pendingFiles, setPendingFiles] = useState<{
    key: string;
    fileName?: string;
  }[]>([]);
  // Per-post cross-post choice. null = follow the account's auto defaults.
  // When the post goes out is no longer chosen here: "Schedule this Post"
  // saves it to the schedule, and the time is set on the Scheduled posts page.
  const [crossPostSelection, setCrossPostSelection] = useState<CrossPostPlatform[] | null>(null);
  const queryKey = ['TagPost', 'tempId', props.pinnedFactiii?.id];
  const moderationRequiredFactiiis = myPostFactiiiCounts.filter(factiii => factiii.isNewFactiii && factiii.requirements.includes(FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED)).map(factiii_0 => factiii_0.name);
  const cameraPhotos = myPostFactiiiCounts.filter(factiii_1 => factiii_1.postFactiiis[0]?.photo && factiii_1.factiiiId === defaultFactiiiIds.camera).map(factiii_2 => ({
    uri: factiii_2.postFactiiis[0].photo!.uri,
    factiiiId: factiii_2.factiiiId
  }));
  const removeCameraPhotoHandler = (factiiiId: number) => {
    setMyPostFactiiiCounts(myPostFactiiiCounts.filter(f => f.factiiiId !== factiiiId));
  };
  const requirementsMet = checkRequirementsMet({
    content,
    postFactiiisCount: [],
    //Empty because the myPostFactiiiCounts is used for validation
    uploads: Object.values(files).map(f_0 => ({
      uri: f_0.uri,
      isVideo: f_0.isVideo
    }))
  }, myPostFactiiiCounts);

  // Find/Set Pinned Tag and auto process if required
  useEffect(() => {
    if (!props.pinnedFactiii) return;
    const pinnedTag = convertPinnedFactiiiToFactiiiTag(props.pinnedFactiii);
    const citingTag: FactiiiTag = props.referencedPostId ? {
      ...pinnedTag,
      postFactiiis: [{
        ...pinnedTag.postFactiiis[0],
        references: [{
          uuid: props.referencedPostId
        }]
      }]
    } : pinnedTag;
    const newPostFactiiiCounts = [...myPostFactiiiCounts, citingTag];
    setMyPostFactiiiCounts(newPostFactiiiCounts);
  }, []);
  const resetAfterSubmit = () => {
    setTitle('');
    setContent('');
    clear();
    setModerationConfModalOpen(false);
    setPrivatePost(false);
    setCrossPostSelection(null);
  };
  const handlePostSuccess = async () => {
    // A post pinned to a board column (a LIST factiii on a space) must refresh
    // that column's cards, whose query key is suffixed `${factiiiId}-${spaceSlug}`.
    if (props.pinnedFactiii && props.spaceSlug) {
      const suffix = `${props.pinnedFactiii.id}-${props.spaceSlug}`;
      await queryClient.invalidateQueries({
        predicate: q => Array.isArray(q.queryKey) && q.queryKey[q.queryKey.length - 1] === suffix
      });
    }
    if (isTopLevelReplyModal) {
      await queryClient.invalidateQueries({
        queryKey: postsQueryKey
      });
    } else {
      props.closeHandler(false);
    }
  };

  // `schedulePlatforms` set means "Schedule this Post": the post is saved as
  // a draft, put on the schedule with those networks, and the author lands on
  // the Scheduled posts page to see or change its time.
  const submitHandler = async (draft: boolean, myFactiiiCounts: FactiiiTag[], force?: boolean, schedulePlatforms?: CrossPostPlatform[]) => {
    setButtonLoading(true);
    const isPrivatePublication = privatePost || props.parentPostId && props.isPrivatePost;

    // For public posts / replies: warn private users before publishing.
    // force=true means we've already confirmed once (e.g. after moderation).
    if (!draft && !force && !isPrivatePublication) {
      const confirmed = await ensureConfirmed(props.parentPostId ? 'reply' : 'post', {
        isSpaceOrFactiii: !!(props.spaceSlug || myFactiiiCounts.length > 0)
      });
      if (!confirmed) {
        setButtonLoading(false);
        return;
      }
    }
    if ((privatePost || props.parentPostId && props.isPrivatePost) && !draft && !refDraftPostId) {
      const createdPost = await createPrivatePost({
        content,
        uploads: Object.values(files).map(f_1 => ({
          uri: f_1.uri
        })),
        factiiis: myFactiiiCounts,
        anonymous: anonymousPost,
        spaceId: undefined,
        parentPostId: props.parentPostId
      });
      if (createdPost) {
        setTitle('');
        setContent('');
        clear();
        setMyPostFactiiiCounts([]);
        // Only reset privatePost if it wasn't forced by parent being private
        if (!props.isPrivatePost) {
          setPrivatePost(false);
        }
        setAnonymousPost(false);
        if (props.parentPostId) {
          await appendReply(createdPost);
          scrollToReply(createdPost.id);
        } else if (localPreferenceState.postModeType === 'private') {
          appendPost(createdPost);
        }
        await handlePostSuccess();
      }
      setButtonLoading(false);
      return;
    }
    if (moderationRequiredFactiiis.length > 0 && !force) {
      return setModerationConfModalOpen(true);
    }

    // Upload photos for factiiis. cameraFactiiiFor gives the watermark context
    // only for a factiii with CAMERA, so every other factiii photo uploads
    // clean. See `.specs/watermarking.md`, "Trigger".
    const factiiiUploads: {
      factiiiId: number;
      upload: UploadType;
    }[] = [];
    for (const factiii_3 of myFactiiiCounts) {
      const photo = factiii_3.postFactiiis[0].photo;
      if (photo?.uri) {
        const dimensions = await getDimensionsFromUri(photo.uri);
        const result = await uploadOne(photo.uri, undefined, dimensions ?? undefined, {
          cameraFactiii: cameraFactiiiFor(factiii_3),
          // The post request puts the photo where the post's files go.
          forPost: true
        });
        if (result) {
          factiiiUploads.push({
            factiiiId: factiii_3.factiiiId,
            upload: result
          });
        }
      }
    }
    const postFactiiiCountsWithUploads: FactiiiTag[] = myFactiiiCounts.map(factiii_4 => {
      const factiiiUpload = factiiiUploads.find(upload_0 => upload_0.factiiiId === factiii_4.id);
      return {
        ...factiii_4,
        postFactiiis: [{
          ...factiii_4.postFactiiis[0],
          uploads: factiiiUpload ? [...(factiii_4.postFactiiis[0].uploads || []), {
            id: factiiiUpload.upload.id,
            display: factiiiUpload.upload.display
          }] : factiii_4.postFactiiis[0].uploads || []
        }, ...factiii_4.postFactiiis.slice(1)]
      };
    });

    // Upload all files on submit
    const uploadedFiles: UploadType[] = [];
    if (Object.keys(files).length > 0) {
      const results = await upload({
        forPost: true
      });
      const resultValues = Object.values(results);
      if (resultValues.length === 0 && Object.keys(files).length > 0) {
        // upload() returns empty record on failure
        setButtonLoading(false);
        return;
      }
      uploadedFiles.push(...resultValues);
    }
    const factiiis = remappedAddFactiiis(postFactiiiCountsWithUploads, queryKey);
    const [, data] = await client.posts.new.mutate({
      ...(title ? {
        title
      } : {}),
      content,
      uploads: uploadedFiles,
      addFactiiis: factiiis.addFactiiis,
      removeFactiiis: refDraftPostId ? factiiis.removeFactiiiIds : undefined,
      spaceSlug: props.spaceSlug,
      anonymousPost,
      refDraftPostId,
      ...(props.parentPostId ? {
        parentPostId: props.parentPostId
      } : {}),
      ...(draft ? {
        saveAsDraft: true
      } : {}),
      ...(crossPostSelection !== null ? {
        crossPostPlatforms: crossPostSelection
      } : {})
    });
    if (data && schedulePlatforms) {
      const [scheduleError] = await client.scheduledPosts.schedule.mutate({
        postId: data.post.id,
        platforms: schedulePlatforms,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      // The reason was already shown. The post is safe either way: it is in
      // Drafts, just not on the schedule.
      if (scheduleError) toast('Saved to your drafts instead', '', 'info');else {
        // The composer also sits on the Scheduled posts page, where the push
        // below goes nowhere and the list would keep showing the old queue.
        await queryClient.invalidateQueries({
          queryKey: ['scheduledPosts']
        });
        router.push(SCHEDULED_POSTS_PATH);
      }
    }
    if (data) {
      // A scheduled draft posted early or saved with new text from here: the
      // Scheduled posts page, which this composer also sits on, has to drop
      // or redraw its row.
      if (refDraftPostId) void queryClient.invalidateQueries({
        queryKey: ['scheduledPosts']
      });
      if (!draft) {
        if (props.parentPostId) {
          await appendReply(data.post);
          scrollToReply(data.post.id);
        } else if (!props.onPostCreated) {
          appendPost(data.post);
        }
        // A published top-level post is the social path's activation.
        if (!props.parentPostId && !props.isBoardPost) {
          trackActivation(currentUser.onboardingPath, 'social');
        }
      }
      eventEmitter.emit('refetch');
      if (!isTopLevelReplyModal && props.onPostCreated) {
        props.onPostCreated(data.post.id);
      }
    }
    resetAfterSubmit();
    await handlePostSuccess();
    setButtonLoading(false);
  };
  const mediaUploadCallbackFn = async (file: File) => {
    const key = `${file.name}-${file.size}-${file.lastModified}`;
    setPendingFiles(prev => [...prev, {
      key,
      fileName: file.name
    }]);
    try {
      const uri = URL.createObjectURL(file);
      const isVideo = file.type.startsWith('video/');
      const dimensions = await getDimensionsFromUri(uri);
      addFile(key, {
        uri,
        fileName: file.name,
        width: dimensions?.width,
        height: dimensions?.height,
        isVideo,
        sizeBytes: file.size,
        mimeType: file.type,
        durationSec: isVideo ? await getVideoDurationFromUri(uri) : null
      });
    } finally {
      setPendingFiles(prev => prev.filter(p => p.key !== key));
    }
  };
  const removeAttachmentHandler = (key_0: string) => {
    removeFile(key_0);
  };
  const draftItemSelectHandler = ({
    content: content_0,
    factiiiOpen: factiiiOpen_0,
    refDraftPostId: refDraftPostId_0,
    anonymousPost: anonymousPost_0
  }: DraftItemSelectHandler) => {
    setRefDraftPostId(refDraftPostId_0);
    setContent(content_0);
    setFactiiiOpen(factiiiOpen_0);
    setDraftsListModalOpen(false);
    setSaveDraftConfModalOpen(false);
    setAnonymousPost(anonymousPost_0);
    setPrivatePost(false);
  };
  const handleCameraPhoto = (photo_0: {
    uri: string;
  }) => {
    const pinnedTag_0 = myPostFactiiiCounts.find(tag => tag.isPinnedFactiii);
    if (!pinnedTag_0) {
      alert('Error: No Pinned Tag Found');
      return;
    }
    const updatedTag: FactiiiTag = {
      ...pinnedTag_0,
      postFactiiis: [{
        ...pinnedTag_0.postFactiiis[0],
        photo: photo_0
      }, ...pinnedTag_0.postFactiiis.slice(1)]
    };
    const newPostFactiiiCounts_0 = myPostFactiiiCounts.map(tag_0 => tag_0.id === pinnedTag_0.id ? updatedTag : tag_0);
    setMyPostFactiiiCounts(newPostFactiiiCounts_0);
    submitHandler(false, newPostFactiiiCounts_0);
  };
  const {
    getRootProps,
    isDragActive
  } = useDropzone({
    onDrop: acceptedFiles => {
      acceptedFiles.forEach(file_0 => mediaUploadCallbackFn(file_0));
    },
    onDropRejected: () => {
      toast('Please drop image, video, or PDF files', '', 'error');
    },
    accept: DROPZONE_ACCEPT,
    noClick: true,
    noKeyboard: true
  });
  const setTitleGuarded = (value: string) => {
    if (value.length <= 255) setTitle(value);
  };

  // The editor keeps what was typed or pasted whatever this does, so content
  // over the limit is kept here too. Dropping it left the counter at 0 and
  // Post disabled with no reason shown. The limit message and the disabled
  // Post button are what stop it (NewPostLayout, NewPostInputSection,
  // submitDisabled).
  const contentTooLong = content.length > MAX_CONTENT_LENGTH;
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): false | Promise<void> => {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (contentTooLong) return false;
      if (!content && !Object.keys(files).length && requirementsMet.flatMap(req => req.results).some(x => !x.completed)) {
        return false;
      }
      submitHandler(false, myPostFactiiiCounts);
    }
    return Promise.resolve();
  };
  const costBreakdown = privatePost ? {
    tagEditFee: 0,
    botCost: 0,
    total: 0
  } : calculateNewPostTronCostBreakdown(myPostFactiiiCounts);
  const {
    insufficientTrons
  } = useTronAffordability(costBreakdown);
  const submitDisabled = buttonLoading || insufficientTrons || contentTooLong || requirementsMet.length > 0 && requirementsMet.flatMap(req_0 => req_0.results).some(x_0 => !x_0.completed) || requirementsMet.length === 0 && !content && Object.keys(files).length === 0;
  const closeOrSaveDraft = () => content ? setSaveDraftConfModalOpen(true) : props.closeHandler();

  // Context value for child components
  const ctx: NewPostContextValue = {
    title,
    setTitle: setTitleGuarded,
    content,
    setContent,
    contentTooLong,
    rows: isReplyMode ? 3 : isTopLevelReplyModal ? 2 : 4,
    label: isReplyMode || isTopLevelReplyModal ? 'Write a reply...' : props.pinnedFactiii ? `Add a ${props.pinnedFactiii.name} post` : 'Join the conversation...',
    onKeyDown: handleKeyDown,
    uploads: Object.values(files),
    uploadingKeys,
    pendingFiles,
    removeAttachmentHandler,
    cameraPhotos,
    removeCameraPhotoHandler,
    factiiiOpen,
    setFactiiiOpen,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    requirementsMet,
    initialLocation: props.initialLocation,
    anonymousPost,
    setAnonymousPost,
    privatePost,
    setPrivatePost,
    refDraftPostId,
    crossPostSelection,
    setCrossPostSelection,
    schedulePost: (platforms: CrossPostPlatform[]) => submitHandler(true, myPostFactiiiCounts, undefined, platforms),
    onDraftsClick: () => setDraftsListModalOpen(true),
    onFilesSelected: (files_0: File[]) => files_0.forEach(file_1 => mediaUploadCallbackFn(file_1)),
    submitHandler: () => submitHandler(false, myPostFactiiiCounts),
    saveDraft: () => submitHandler(true, myPostFactiiiCounts),
    forceSubmit: () => submitHandler(false, myPostFactiiiCounts, true),
    submitDisabled,
    buttonLoading,
    submitButtonText: isReplyMode || isTopLevelReplyModal ? 'Reply' : 'Post',
    pinnedFactiii: props.pinnedFactiii,
    queryKey,
    postId: refDraftPostId || 'tempId',
    // DO NOT CHANGE THIS, it is used in the backend
    isPrivate: privatePost,
    isOpened: factiiiOpen,
    factiiiScrollable: isTopLevelReplyModal,
    toolbarPt: isReplyMode || isTopLevelReplyModal ? 'pt-1' : 'pt-2',
    closeHandler: props.closeHandler,
    closeOrSaveDraft,
    moderationConfModalOpen,
    setModerationConfModalOpen,
    moderationRequiredFactiiis,
    saveDraftConfModalOpen,
    setSaveDraftConfModalOpen,
    draftsListModalOpen,
    setDraftsListModalOpen,
    draftItemSelectHandler,
    isReplyMode,
    isTopLevelReplyModal,
    inline: props.inline,
    isBoardPost: props.isBoardPost,
    spaceSlug: props.spaceSlug,
    parentPostId: props.parentPostId,
    parentPost: props.parentPost,
    parentIsPrivate: Boolean(props.parentPostId && props.isPrivatePost),
    uniqueId: replyUniqueId,
    scrollAreaRef
  };
  return {
    ctx,
    autoProcess,
    setAutoProcess,
    handleCameraPhoto,
    getRootProps,
    isDragActive,
    privateConfirmDialog
  };
};